import { useCallback, useState } from 'react'
import { css } from '@emotion/react'
import {
  DesignToken,
  type ITooltipProps,
  Tooltip,
} from '@livechat/design-system-react-components'

import { isAppInstalled, isAuthorized } from '@/store/selectors'
import { useTypedSelector } from '@/store/hooks'
import useParamSelector from '@/hooks/useParamSelector'
import { type IDeveloperProfile } from '@/apis/apps/interfaces'
import VotingStars from '../VotingStars'
import RatingModal from '../Modal'
import NotLoggedIn from './NotLoggedIn'
import RateExperience from './RateExperience'

const tooltipCss = css`
  min-width: 290px;
`

const rateActionCss = (votes: number) =>
  css`
    ${!votes && 'text-transform: lowercase;'}
    color: var(${DesignToken.ActionPrimaryDefault});

    &:hover {
      color: var(${DesignToken.ActionPrimaryHover});
    }
  `

interface Props {
  id: string
  votes: number
  withStars: boolean
  placement: ITooltipProps['placement']
  developer: IDeveloperProfile // TODO: verify type
}

const RatingTooltip = ({
  id,
  votes,
  withStars,
  placement = 'bottom',
  developer,
}: Props) => {
  const [rate, setRate] = useState<number | null>(null)
  const [isVisible, setIsVisible] = useState<boolean>(false)
  const [isRatingModalOpen, setRatingModalOpen] = useState<boolean>(false)

  const isLoggedIn: boolean = useTypedSelector(isAuthorized)
  const isInstalled = useParamSelector(isAppInstalled, id)

  const toggleTooltip = useCallback(() => setIsVisible((prev) => !prev), [])
  const toggleRatingModal = useCallback(() => {
    setRatingModalOpen((prev) => !prev)
    setIsVisible(false)
  }, [])
  const rateAndOpenTooltip = useCallback(
    (rate: number) => {
      setRate(rate)

      if (!isVisible) {
        toggleTooltip()
      }
    },
    [isVisible, toggleTooltip]
  )

  const triggerComponent = withStars ? (
    <div>
      <VotingStars rate={rate} setRate={rateAndOpenTooltip} />
    </div>
  ) : (
    <a href="#rate" css={rateActionCss(votes)} onClick={toggleTooltip}>
      Rate this app
    </a>
  )

  return (
    <>
      <Tooltip
        css={tooltipCss}
        placement={placement}
        triggerRenderer={triggerComponent}
        isVisible={isVisible}
        onClose={toggleTooltip}
        triggerOnHover={false}
      >
        {isLoggedIn && isInstalled ? (
          <RateExperience
            id={id}
            rate={rate}
            setRate={setRate}
            onClose={toggleTooltip}
            toggleRatingModal={toggleRatingModal}
            developer={developer}
          />
        ) : (
          <NotLoggedIn onClose={toggleTooltip} />
        )}
      </Tooltip>

      {isRatingModalOpen && (
        <RatingModal
          id={id}
          rate={rate}
          onClose={toggleRatingModal}
          displayFormOnInit
        />
      )}
    </>
  )
}

export default RatingTooltip
